import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import {
    FlowDashboardData,
    getFlowAccumulation,
    getFlowAxis,
} from 'src/app/services/flow-dashboard/flow-dashboard.models';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardDecoActivitySqlMap } from '../../mopa-resources-detailed/sql-maps/biviz-dashboard-deco-activity-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { getDecoFlowMetric } from '../deco-flow.models';

/**
 * Deco Flow on a cohort lifetime-day axis (Install Day or Active Day) — the decoration twin of
 * `resource-flow-by-day-no.ts`, reading `inflow_delta` (decorations gained) and `new_placed_delta`
 * (first-time placements) instead of a currency pair.
 *
 * Cohort semantics, the axis-eligible denominator and the RUNNING definition are identical;
 * see the Resource Flow loader's header for the reasoning behind each.
 */

/** Ignored on cohort axes. */
const IGNORED_ON_DAY_NO_AXIS = [
    'ACTIVITY_DATE',
    'ACTIVITY_APP_VERSION',
    'ACTIVITY_APP_VERSION_GROUP_MINOR',
];

export interface DecoFlowByDayNoDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

export class DecoFlowByDayNoDataLoaderFactory extends Factory<
    DataLoader,
    DecoFlowByDayNoDataLoaderFactoryOptions
> {
    override FACTORY_CLASS = 'DecoFlowByDayNoDataLoaderFactory';

    getInstance(options: DecoFlowByDayNoDataLoaderFactoryOptions): DataLoader {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                const axis = getFlowAxis(queryContext);
                if (axis === 'date') {
                    return;
                }
                const isActiveDayAxis = axis === 'active_day';

                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const decoSqlMap = BiVizDashboardDecoActivitySqlMap.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    decoSqlMap.columns
                );
                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);
                dsColumns.present = dsColumns.present.filter(
                    (column) => !IGNORED_ON_DAY_NO_AXIS.includes(column.id)
                );

                const isRunning =
                    getFlowAccumulation(queryContext) === 'running';
                const maxDayNoSQL = '90';
                const decoSQL =
                    BiVizDashboardDecoActivitySqlMap.sqlStatementsCompiler.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                    );

                const metric = getDecoFlowMetric(queryContext);
                const { statistic, population, percentiles } = metric;

                if (statistic === 'percentile' && !percentiles.length) {
                    throw new Error(
                        'Deco Flow: percentile mode needs at least one selected percentile'
                    );
                }

                const splitsByPercentile =
                    statistic === 'percentile' && percentiles.length > 1;

                // Per-DAU population is independent of decoration-specific flow filters.
                const usersdashActivity = BiVizDashboardUsersActivity;
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );
                usersdashActivityDsColumns.present =
                    usersdashActivityDsColumns.present.filter(
                        (column) => !IGNORED_ON_DAY_NO_AXIS.includes(column.id)
                    );
                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options
                    );

                const decoTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    decoSqlMap.dataset,
                    decoSqlMap.tableId
                );
                const usersdashActivityTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    usersdashActivity.sqlMap.dataset,
                    usersdashActivity.sqlMap.tableId
                );

                let flowAggregationSQL: string;

                if (statistic === 'total') {
                    flowAggregationSQL = `
, flow_agg AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
    , SUM(inflow_delta)     AS inflow
    , SUM(new_placed_delta) AS outflow
  FROM base
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
)`;
                } else if (statistic === 'flow records') {
                    flowAggregationSQL = `
, flow_agg AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
    , COUNTIF(inflow_delta > 0)     AS inflow
    , COUNTIF(new_placed_delta > 0) AS outflow
  FROM base
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
)`;
                } else if (statistic === 'players') {
                    flowAggregationSQL = `
, flow_agg AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
    , COUNT(DISTINCT IF(inflow_delta > 0, player_id, NULL))     AS inflow
    , COUNT(DISTINCT IF(new_placed_delta > 0, player_id, NULL)) AS outflow
  FROM base
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
)`;
                } else if (statistic === 'average' && population === 'deu') {
                    // Each side divides by its own engaged players on that cohort day.
                    flowAggregationSQL = `
, flow_agg AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
    , SAFE_DIVIDE(
        SUM(inflow_delta),
        COUNT(DISTINCT IF(inflow_delta > 0, player_id, NULL))
      ) AS inflow
    , SAFE_DIVIDE(
        SUM(new_placed_delta),
        COUNT(DISTINCT IF(new_placed_delta > 0, player_id, NULL))
      ) AS outflow
  FROM base
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
)`;
                } else if (statistic === 'average' && population === 'dau') {
                    // Install Day uses D0; Active Day uses players who reached the day.
                    flowAggregationSQL = `
, cohort_sizes AS
(
  SELECT
    ${decoSQL.splitGroupBy}day_no
    , SUM(cohort_size) AS cohort_size
  FROM
  (
    SELECT
      ${decoSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
      first_open_date
      , day_no
      , COUNT(DISTINCT player_id) AS cohort_size
    FROM cohort
    CROSS JOIN UNNEST(GENERATE_ARRAY(0, ${
        isActiveDayAxis
            ? `COALESCE(LEAST(max_active_day_no, ${maxDayNoSQL}), 0)`
            : maxDayNoSQL
    })) AS day_no
    ${
        decoSQL.is_in_context_split_datasource_specific_columns
            ? `CROSS JOIN (SELECT DISTINCT ${decoSQL.datasourceSpecificSplitGroupAlias} FROM base)`
            : ''
    }
    GROUP BY ${decoSQL.splitGroupBy}first_open_date, day_no
  )
  GROUP BY ${decoSQL.splitGroupBy}day_no
)
, deco_agg AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
    , SUM(inflow_delta)     AS sum_inflow
    , SUM(new_placed_delta) AS sum_outflow
  FROM base
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
)
, flow_agg AS
(
  SELECT
    ${decoSQL.splitGroup ? 'deco_agg.split_group AS split_group, ' : ''}${
        decoSQL.bucketGroup ? 'deco_agg.bucket_group AS bucket_group, ' : ''
    }deco_agg.day_no AS day_no
    , SAFE_DIVIDE(deco_agg.sum_inflow, cohort_sizes.cohort_size)  AS inflow
    , SAFE_DIVIDE(deco_agg.sum_outflow, cohort_sizes.cohort_size) AS outflow
  FROM deco_agg
  LEFT JOIN cohort_sizes
    ON deco_agg.day_no = cohort_sizes.day_no
    ${
        decoSQL.splitGroup
            ? "AND IFNULL(deco_agg.split_group, '_NULL_') = IFNULL(cohort_sizes.split_group, '_NULL_')"
            : ''
    }
)`;
                } else if (statistic === 'percentile' && population === 'deu') {
                    // A percentile describes one player, so rows are summed per player and cohort
                    // day first. `deu` keeps only the players with a positive flow on that side.
                    flowAggregationSQL = `
, per_player AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
    , player_id
    , SUM(inflow_delta)     AS inflow
    , SUM(new_placed_delta) AS outflow
  FROM base
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no, player_id
)
, flow_agg AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
    , APPROX_QUANTILES(IF(inflow > 0, inflow, NULL), 100)[OFFSET(${percentiles[0]})]   AS inflow
    , APPROX_QUANTILES(IF(outflow > 0, outflow, NULL), 100)[OFFSET(${percentiles[0]})] AS outflow${
        splitsByPercentile
            ? percentiles
                  .map(
                      (percentile) => `
    , APPROX_QUANTILES(IF(inflow > 0, inflow, NULL), 100)[OFFSET(${percentile})]   AS quantile_inflow_${percentile}
    , APPROX_QUANTILES(IF(outflow > 0, outflow, NULL), 100)[OFFSET(${percentile})] AS quantile_outflow_${percentile}`
                  )
                  .join('')
            : ''
    }
  FROM per_player
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
)`;
                } else if (statistic === 'percentile' && population === 'dau') {
                    flowAggregationSQL = `
, per_player AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
    , player_id
    , SUM(inflow_delta)     AS inflow
    , SUM(new_placed_delta) AS outflow
  FROM base
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no, player_id
)
, cohort_players AS
(
  SELECT
    ${decoSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    day_no
    , player_id
  FROM
  (
    SELECT ${decoSQL.cohortSplitGroupBy}d.day_no AS day_no
      , c.player_id AS player_id
    FROM days d
    ${
        isActiveDayAxis
            ? 'JOIN cohort c ON c.max_active_day_no >= d.day_no'
            : 'CROSS JOIN cohort c'
    }
  )
  ${
      decoSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${decoSQL.datasourceSpecificSplitGroupAlias} FROM base)`
          : ''
  }
  ${
      decoSQL.bucketGroup
          ? 'CROSS JOIN (SELECT DISTINCT bucket_group FROM base)'
          : ''
  }
)
, population AS
(
  SELECT ${
      decoSQL.splitGroup ? 'cohort_players.split_group AS split_group, ' : ''
  }${
      decoSQL.bucketGroup ? 'cohort_players.bucket_group AS bucket_group, ' : ''
  }cohort_players.day_no AS day_no
    , COALESCE(per_player.inflow, 0)  AS inflow
    , COALESCE(per_player.outflow, 0) AS outflow
  FROM cohort_players
  LEFT JOIN per_player
    ON per_player.day_no = cohort_players.day_no
   AND per_player.player_id = cohort_players.player_id
   ${
       /* A Split or Group over a nullable column (e.g. `income_type` is NULL on placement rows)
          yields a NULL group, which plain equality would never match. */
       decoSQL.splitGroup
           ? `AND IFNULL(per_player.split_group, '_NULL_') = IFNULL(cohort_players.split_group, '_NULL_')`
           : ''
   }
   ${
       decoSQL.bucketGroup
           ? `AND IFNULL(per_player.bucket_group, '_NULL_') = IFNULL(cohort_players.bucket_group, '_NULL_')`
           : ''
   }
)
, flow_agg AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
    , APPROX_QUANTILES(inflow, 100)[OFFSET(${percentiles[0]})]  AS inflow
    , APPROX_QUANTILES(outflow, 100)[OFFSET(${percentiles[0]})] AS outflow${
        splitsByPercentile
            ? percentiles
                  .map(
                      (percentile) => `
    , APPROX_QUANTILES(inflow, 100)[OFFSET(${percentile})]  AS quantile_inflow_${percentile}
    , APPROX_QUANTILES(outflow, 100)[OFFSET(${percentile})] AS quantile_outflow_${percentile}`
                  )
                  .join('')
            : ''
    }
  FROM population
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}day_no
)`;
                } else {
                    // A new statistic or population must state its own query instead of silently
                    // inheriting another one.
                    throw new Error(
                        `Deco Flow: no lifetime-day query defined for statistic '${statistic}' and population '${population}'`
                    );
                }

                const query = `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow (${
                    isActiveDayAxis ? 'active day' : 'install day'
                } axis) - mode: ${metric.label}, accumulation: ${
                    isRunning ? 'running' : 'daily'
                }
WITH cohort AS
(
  SELECT
${
    usersdashActivitySQL.splitGroupBy
        ? usersdashActivitySQL.splitSelect.replace(
              'AS split_group',
              'AS cohort_split_group'
          )
        : ''
}
    player_id
    , ${decoSqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL} AS first_open_date
    ${isActiveDayAxis ? ', MAX(ds.active_day_no) AS max_active_day_no' : ''}
  FROM \`${usersdashActivityTablePath}\` ds
  ${usersdashActivitySQL.join}
  ${usersdashActivitySQL.where}
  GROUP BY ${decoSQL.cohortSplitGroupBy}${
      decoSqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL
  }, player_id
)
, days AS
(
  SELECT day_no
  FROM UNNEST(GENERATE_ARRAY(0, ${maxDayNoSQL})) AS day_no
)
, base AS
(
  SELECT
    ${decoSQL.splitSelect}
    ${decoSQL.datasourceSpecificSplitSelect}
    ${decoSQL.cohortSplitSelect}
    player_day.${isActiveDayAxis ? 'active_day_no' : 'install_day_no'} AS day_no
    , ds.player_id
    , ds.inflow_delta
    , ds.new_placed_delta
    ${decoSQL.bucketSelect ? `, ${decoSQL.bucketSelect}` : ''}
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
)
${flowAggregationSQL}
, grid AS
(
  SELECT
    ${decoSQL.splitGroup ? 'g.split_group, ' : ''}${
        decoSQL.bucketGroup ? 'g.bucket_group, ' : ''
    }d.day_no AS day_no
  FROM days d
  ${
      decoSQL.splitGroup || decoSQL.bucketGroup
          ? `CROSS JOIN
  (
    SELECT DISTINCT
      ${decoSQL.splitGroup ? 'split_group' : ''}${
          decoSQL.splitGroup && decoSQL.bucketGroup ? ', ' : ''
      }${decoSQL.bucketGroup ? 'bucket_group' : ''}
    FROM flow_agg
  ) g`
          : ''
  }
)

SELECT
    ${decoSQL.splitGroup ? 'grid.split_group AS split_group\n  , ' : ''}${
        decoSQL.bucketGroup ? 'grid.bucket_group AS bucket_group\n  , ' : ''
    }CAST(grid.day_no AS STRING) AS day_no
  , ${
      isRunning
          ? `ROUND(SUM(COALESCE(flow_agg.inflow, 0)) OVER (${
                decoSQL.splitGroup
                    ? `PARTITION BY grid.split_group${
                          decoSQL.bucketGroup ? ', grid.bucket_group' : ''
                      } `
                    : decoSQL.bucketGroup
                      ? 'PARTITION BY grid.bucket_group '
                      : ''
            }ORDER BY grid.day_no), 2)`
          : 'ROUND(COALESCE(flow_agg.inflow, 0), 2)'
  } AS inflow
  , ${
      isRunning
          ? `ROUND(SUM(COALESCE(flow_agg.outflow, 0)) OVER (${
                decoSQL.splitGroup
                    ? `PARTITION BY grid.split_group${
                          decoSQL.bucketGroup ? ', grid.bucket_group' : ''
                      } `
                    : decoSQL.bucketGroup
                      ? 'PARTITION BY grid.bucket_group '
                      : ''
            }ORDER BY grid.day_no), 2)`
          : 'ROUND(COALESCE(flow_agg.outflow, 0), 2)'
  } AS outflow${
      splitsByPercentile
          ? percentiles
                .map(
                    (percentile) => `
  , ${
      isRunning
          ? `ROUND(SUM(COALESCE(flow_agg.quantile_inflow_${percentile}, 0)) OVER (${
                decoSQL.splitGroup
                    ? `PARTITION BY grid.split_group${
                          decoSQL.bucketGroup ? ', grid.bucket_group' : ''
                      } `
                    : decoSQL.bucketGroup
                      ? 'PARTITION BY grid.bucket_group '
                      : ''
            }ORDER BY grid.day_no), 2)`
          : `ROUND(COALESCE(flow_agg.quantile_inflow_${percentile}, 0), 2)`
  } AS quantile_inflow_${percentile}
  , ${
      isRunning
          ? `ROUND(SUM(COALESCE(flow_agg.quantile_outflow_${percentile}, 0)) OVER (${
                decoSQL.splitGroup
                    ? `PARTITION BY grid.split_group${
                          decoSQL.bucketGroup ? ', grid.bucket_group' : ''
                      } `
                    : decoSQL.bucketGroup
                      ? 'PARTITION BY grid.bucket_group '
                      : ''
            }ORDER BY grid.day_no), 2)`
          : `ROUND(COALESCE(flow_agg.quantile_outflow_${percentile}, 0), 2)`
  } AS quantile_outflow_${percentile}`
                )
                .join('')
          : ''
  }
FROM grid
LEFT JOIN flow_agg
  ON flow_agg.day_no = grid.day_no
  ${
      decoSQL.splitGroup
          ? "AND IFNULL(flow_agg.split_group, '_NULL_') = IFNULL(grid.split_group, '_NULL_')"
          : ''
  }
  ${
      decoSQL.bucketGroup
          ? "AND IFNULL(flow_agg.bucket_group, '_NULL_') = IFNULL(grid.bucket_group, '_NULL_')"
          : ''
  }
ORDER BY ${decoSQL.splitGroup ? 'grid.split_group, ' : ''}${
                    decoSQL.bucketGroup ? 'grid.bucket_group, ' : ''
                }grid.day_no
`;
                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                const resultRows = [...queryResponse.result];
                // The axis is densified in SQL, so there is no fillMissingTimeGapRows step here.
                const rows = splitsByPercentile
                    ? resultRows.flatMap((row) =>
                          percentiles.map((percentile) => {
                              const label = `P${percentile}`;
                              return {
                                  ...row,
                                  inflow: row[`quantile_inflow_${percentile}`],
                                  outflow:
                                      row[`quantile_outflow_${percentile}`],
                                  split_group: decoSQL.splitGroup
                                      ? `${row.split_group ?? 'Total'} · ${label}`
                                      : label,
                                  ...(decoSQL.bucketGroup
                                      ? {
                                            bucket_group: `${
                                                row.bucket_group ?? 'Unknown'
                                            } · ${label}`,
                                        }
                                      : {}),
                              };
                          })
                      )
                    : resultRows;

                const data: FlowDashboardData = {
                    rows,
                    rangeAggregation: metric.rangeAggregation,
                };
                this.latestData = data;
                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
